import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PrivacyTipRoundedIcon from "@mui/icons-material/PrivacyTipRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LanguageControl } from "@/components/i18n/LanguageControl";
import { useLegalDocumentDialog } from "@/components/legal/useLegalDocumentDialog";
import { GeoAnalyticsFooter } from "@/components/layout/GeoAnalyticsFooter";
import { ThemeModeControl } from "@/components/theme/ThemeModeControl";
import { signOutOfGeoAnalytics } from "@/features/auth/authService";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { geoVisualAssets } from "@/theme/visualAssets";
import { useAuth } from "@/features/auth/authContext";
import {
  administrationAccessRoles,
  getUserPlatformRoles,
  projectAdministrationRoles,
  projectTechnicalRoles,
  projectWorkspaceRoles,
  supportPortalRoles,
  teamWorkspaceRoles,
  type PlatformRole
} from "@/features/auth/roles";
import {
  recordUserActivity,
  type RecordUserActivityInput
} from "@/features/activity/activityService";
import { useNotifications } from "@/features/notifications/useNotifications";

const drawerWidth = 80;

type NavigationItem = {
  labelKey: string;
  path: string;
  icon: ReactNode;
  roles?: readonly PlatformRole[];
};

const customerNavigationItems = [
  {
    labelKey: "nav.dashboard",
    path: "/painel",
    icon: <DashboardRoundedIcon />,
    roles: projectWorkspaceRoles
  },
  {
    labelKey: "nav.projects",
    path: "/projetos",
    icon: <FolderRoundedIcon />,
    roles: projectWorkspaceRoles
  },
  {
    labelKey: "nav.teams",
    path: "/equipes",
    icon: <GroupsRoundedIcon />,
    roles: teamWorkspaceRoles
  },
  {
    labelKey: "nav.notifications",
    path: "/notificacoes",
    icon: <NotificationsRoundedIcon />,
    roles: projectWorkspaceRoles
  },
  {
    labelKey: "nav.support",
    path: "/suporte",
    icon: <SupportAgentRoundedIcon />,
    roles: supportPortalRoles
  }
] as const satisfies readonly NavigationItem[];

const administrativeNavigationItems = [
  {
    labelKey: "nav.administration",
    path: "/administracao",
    icon: <AdminPanelSettingsRoundedIcon />,
    roles: administrationAccessRoles
  },
  {
    labelKey: "nav.studio",
    path: "/studio",
    icon: <DesignServicesRoundedIcon />,
    roles: projectTechnicalRoles
  }
] as const satisfies readonly NavigationItem[];

const contextualNavigationItems = [
  {
    labelKey: "nav.newProcess",
    path: "/processos/novo",
    icon: <AddBusinessRoundedIcon />,
    roles: projectAdministrationRoles
  },
  {
    labelKey: "nav.user",
    path: "/usuario",
    icon: <PersonRoundedIcon />
  }
] as const satisfies readonly NavigationItem[];

const allNavigationItems = [
  ...customerNavigationItems,
  ...administrativeNavigationItems,
  ...contextualNavigationItems
];

const routeActivity: Record<string, RecordUserActivityInput> = {
  "/projetos": {
    type: "project",
    title: "Processos",
    description: "Carteira de processos REURB aberta",
    path: "/projetos"
  },
  "/processos/novo": {
    type: "project",
    title: "Novo processo",
    description: "Criação orientada de processo REURB aberta",
    path: "/processos/novo"
  },
  "/equipes": {
    type: "team",
    title: "Equipes",
    description: "Área de colaboração aberta",
    path: "/equipes"
  },
  "/notificacoes": {
    type: "notification",
    title: "Notificações",
    description: "Central de avisos aberta",
    path: "/notificacoes"
  },
  "/suporte": {
    type: "support",
    title: "Suporte",
    description: "Atendimento aberto",
    path: "/suporte"
  }
};

function filterNavigationByRoles(items: readonly NavigationItem[], roles: readonly PlatformRole[]) {
  return items.filter((item) => {
    if (!item.roles?.length) {
      return true;
    }

    return item.roles.some((role) => roles.includes(role));
  });
}

function NavigationItemButton({ item }: { item: NavigationItem }) {
  const { t } = useTranslation();

  return (
    <Tooltip
      key={item.path}
      title={t(item.labelKey)}
      describeChild
      placement="right"
      disableInteractive
    >
      <ListItemButton
        component={NavLink}
        to={item.path}
        aria-label={t(item.labelKey)}
        sx={(theme) => ({
          borderRadius: 1,
          minHeight: 48,
          justifyContent: "center",
          color: "text.secondary",
          px: 1,
          my: 0.35,
          overflow: "hidden",
          transition: theme.transitions.create(["background-color", "color"], {
            duration: theme.transitions.duration.shorter
          }),
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2
          },
          "&.active": {
            bgcolor: "action.selected",
            color: "primary.main",
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
          }
        })}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            color: "inherit",
            justifyContent: "center"
          }}
        >
          {item.icon}
        </ListItemIcon>
      </ListItemButton>
    </Tooltip>
  );
}

type NavigationListProps = {
  primaryItems: readonly NavigationItem[];
  adminItems: readonly NavigationItem[];
};

function NavigationList({ primaryItems, adminItems }: NavigationListProps) {
  return (
    <List sx={{ px: 1, py: 1.5 }}>
      {primaryItems.map((item) => (
        <NavigationItemButton key={item.path} item={item} />
      ))}
      {adminItems.length ? <Divider sx={{ my: 1 }} /> : null}
      {adminItems.map((item) => (
        <NavigationItemButton key={item.path} item={item} />
      ))}
    </List>
  );
}

function getInitials(name?: string | null, email?: string | null) {
  const source = name?.trim() || email?.trim() || "GA";
  const words = source.split(/\s+/).filter(Boolean);

  if (words.length >= 2) {
    return `${words[0]?.[0] ?? ""}${words[1]?.[0] ?? ""}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
}

export function AppShell() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { firebaseUser, profile } = useAuth();
  const { notifications, unreadCount } = useNotifications(firebaseUser?.uid);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(null);
  const lastRecordedActivityRef = useRef<string | null>(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const navigate = useNavigate();
  const roles = getUserPlatformRoles(profile);
  const primaryItems = filterNavigationByRoles(customerNavigationItems, roles);
  const adminItems = filterNavigationByRoles(administrativeNavigationItems, roles);
  const activeItem =
    allNavigationItems.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    ) ?? allNavigationItems[0];
  const activePath = activeItem?.path ?? "/painel";
  const mobileItems = [...primaryItems, ...adminItems];
  const avatarUrl = profile?.photoURL ?? firebaseUser?.photoURL ?? undefined;
  const displayName = profile?.displayName ?? firebaseUser?.displayName ?? "Usuário GeoAnalytics";
  const initials = getInitials(displayName, profile?.email ?? firebaseUser?.email);
  const primaryOrganizationId = profile?.organizationIds?.[0];
  const recentNotifications = notifications.slice(0, 4);
  const { openLegalDocument, legalDocumentDialog } = useLegalDocumentDialog();

  useEffect(() => {
    const uid = firebaseUser?.uid;
    const activity = routeActivity[location.pathname];

    if (!uid || !activity) {
      return;
    }

    const activityKey = `${uid}:${location.pathname}`;

    if (lastRecordedActivityRef.current === activityKey) {
      return;
    }

    lastRecordedActivityRef.current = activityKey;
    void recordUserActivity(uid, {
      ...activity,
      organizationId: primaryOrganizationId
    }).catch(() => {
      lastRecordedActivityRef.current = null;
    });
  }, [firebaseUser?.uid, location.pathname, primaryOrganizationId]);

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh", bgcolor: "background.default" }}>
      {isDesktop ? (
        <Drawer
          variant="permanent"
          data-geo-no-auto-translate
          translate="no"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              overflowX: "hidden",
              overflowY: "auto",
              scrollbarWidth: "none",
              borderRight: 1,
              borderColor: "divider",
              "&::-webkit-scrollbar": {
                display: "none"
              }
            }
          }}
        >
          <Toolbar sx={{ px: { xs: 1.5, sm: 1.5 }, justifyContent: "center" }}>
            <Box
              component="img"
              src={geoVisualAssets.logo[theme.palette.mode]}
              alt="GeoAnalytics"
              sx={{
                width: 44,
                height: 48,
                flex: "0 0 auto",
                display: "block",
                objectFit: "contain"
              }}
            />
          </Toolbar>
          <Divider />
          <NavigationList primaryItems={primaryItems} adminItems={adminItems} />
        </Drawer>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pb: isDesktop ? 0 : 9.5,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <AppBar
          className="geo-page-header"
          position="sticky"
          color="inherit"
          elevation={0}
          data-geo-no-auto-translate
          translate="no"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            overflow: "hidden"
          }}
        >
          <Toolbar
            sx={{
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              rowGap: { xs: 0.5, sm: 0 },
              gap: { xs: 0.5, sm: 2 },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 0 },
              minHeight: { xs: 112, sm: 64 },
              minWidth: 0,
              overflowX: "hidden",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none"
              }
            }}
          >
            <Typography
              variant="h2"
              sx={{
                flex: { xs: "1 1 100%", sm: "0 0 auto" },
                width: { xs: "100%", sm: "auto" },
                fontSize: "1.125rem",
                maxWidth: { xs: "100%", sm: "none" },
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {activeItem ? t(activeItem.labelKey) : "GeoAnalytics"}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                flex: "0 0 auto",
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "flex-end", sm: "initial" },
                ml: "auto"
              }}
            >
              <LanguageControl />
              <Tooltip title={t("shell.notifications")}>
                <IconButton
                  aria-label={t("shell.openNotifications")}
                  size="small"
                  onClick={(event) => setNotificationAnchorEl(event.currentTarget)}
                >
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationsRoundedIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={() => setNotificationAnchorEl(null)}
                slotProps={{
                  paper: {
                    sx: {
                      width: { xs: 320, sm: 380 },
                      maxWidth: "calc(100vw - 24px)"
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography sx={{ fontWeight: 900, flex: 1 }}>Notificações</Typography>
                    <Chip
                      size="small"
                      color={unreadCount ? "primary" : "default"}
                      label={`${unreadCount} nova(s)`}
                    />
                  </Stack>
                </Box>
                <Divider />
                {recentNotifications.length ? (
                  recentNotifications.map((notification) => (
                    <MenuItem
                      key={notification.id}
                      onClick={() => {
                        setNotificationAnchorEl(null);
                        void navigate("/notificacoes");
                      }}
                      sx={{ alignItems: "flex-start", whiteSpace: "normal", py: 1.25 }}
                    >
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{ alignItems: "center", minWidth: 0 }}
                          >
                            <Typography sx={{ fontWeight: 900 }} noWrap>
                              {notification.title}
                            </Typography>
                            {notification.status === "unread" ? (
                              <Chip label="nova" size="small" color="primary" />
                            ) : null}
                          </Stack>
                        }
                        secondary={notification.summary || notification.message}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <Box sx={{ px: 2, py: 2 }}>
                    <Typography color="text.secondary">
                      Nenhuma notificação recente para revisar.
                    </Typography>
                  </Box>
                )}
                <Divider />
                <Box sx={{ p: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setNotificationAnchorEl(null);
                      void navigate("/notificacoes");
                    }}
                  >
                    Abrir central de notificações
                  </Button>
                </Box>
              </Menu>
              <ThemeModeControl />
              <Tooltip title={t("shell.profileTooltip")}>
                <IconButton
                  aria-label={t("shell.profileAria")}
                  size="small"
                  aria-haspopup="menu"
                  aria-expanded={profileAnchorEl ? "true" : undefined}
                  onClick={(event) => setProfileAnchorEl(event.currentTarget)}
                >
                  <Avatar
                    src={avatarUrl}
                    alt={displayName}
                    sx={{
                      width: 34,
                      height: 34,
                      fontSize: "0.8125rem",
                      fontWeight: 800,
                      bgcolor: "primary.light",
                      color: "primary.dark",
                      border: 2,
                      borderColor: "primary.main"
                    }}
                  >
                    {initials}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={profileAnchorEl}
                open={Boolean(profileAnchorEl)}
                onClose={() => setProfileAnchorEl(null)}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: 230,
                      maxWidth: "calc(100vw - 24px)"
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Typography sx={{ fontWeight: 900 }} noWrap>
                    {displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {profile?.email ?? firebaseUser?.email ?? "Conta GeoAnalytics"}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setProfileAnchorEl(null);
                    void navigate("/usuario");
                  }}
                >
                  <ListItemIcon>
                    <PersonRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Perfil" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setProfileAnchorEl(null);
                    openLegalDocument("terms");
                  }}
                >
                  <ListItemIcon>
                    <ArticleRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Termos de uso" />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setProfileAnchorEl(null);
                    openLegalDocument("privacy");
                  }}
                >
                  <ListItemIcon>
                    <PrivacyTipRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Política de uso" />
                </MenuItem>
              </Menu>
              {legalDocumentDialog}
              <Tooltip title={t("shell.signOut")}>
                <IconButton
                  aria-label={t("shell.signOut")}
                  edge="end"
                  onClick={() => void signOutOfGeoAnalytics()}
                >
                  <LogoutRoundedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            flex: 1,
            minHeight: { xs: "calc(100dvh - 112px)", sm: "calc(100dvh - 64px)" }
          }}
        >
          <Outlet />
        </Box>
        <GeoAnalyticsFooter compact />
      </Box>
      {!isDesktop ? (
        <BottomNavigation
          aria-label="Navegação principal"
          data-geo-no-auto-translate
          translate="no"
          value={activePath}
          onChange={(_event, value: string) => {
            if (value === "__signout") {
              void signOutOfGeoAnalytics();
              return;
            }

            void navigate(value);
          }}
          showLabels
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: "calc(76px + env(safe-area-inset-bottom))",
            paddingBottom: "env(safe-area-inset-bottom)",
            justifyContent: "flex-start",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            borderTop: 1,
            borderColor: "divider",
            zIndex: theme.zIndex.drawer + 1,
            "&::-webkit-scrollbar": {
              display: "none"
            },
            "& .MuiBottomNavigationAction-root": {
              flex: "0 0 82px",
              minWidth: 82,
              maxWidth: 90,
              px: 0.75
            },
            "& .MuiBottomNavigationAction-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%"
            }
          }}
        >
          {mobileItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={t(item.labelKey)}
              value={item.path}
              icon={item.icon}
            />
          ))}
          <BottomNavigationAction
            label={t("shell.signOut")}
            value="__signout"
            icon={<LogoutRoundedIcon />}
          />
        </BottomNavigation>
      ) : null}
    </Box>
  );
}
